import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Send, Loader2, Trophy, MessageSquare, Sparkles, Globe, Download, ArrowUpRight, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// One home for community features that keep the platform human:
// - Pay from Heart (voluntary donation)
// - Top Supporters wall (top-100)
// - Suggestion Board (auto-thank + upvote)

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Community() {
  useSeo({ title: "Community · Pay from Heart · Find2Near", description: "Donate, suggest features, and see the supporters keeping Find2Near free for everyone." });
  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-6 space-y-8" data-testid="community-page">
      <header>
        <h1 className="heading text-4xl md:text-5xl font-extrabold text-stone-900 leading-tight">Our community, our engine</h1>
        <p className="text-stone-600 mt-2 text-sm max-w-2xl">Find2Near is free for citizens across the world. Voluntary contributions from users, brands, and countries keep it that way — and every suggestion you send lands directly in front of our team.</p>
      </header>

      <PayFromHeart />
      <TopSupporters />
      <SuggestionBoard />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pay from Heart
// -----------------------------------------------------------------------------
const PRESETS_INR = [11, 51, 101, 501, 1001, 5001];

function PayFromHeart() {
  const { user } = useAuth();
  const [amount, setAmount] = useState(101);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = custom ? parseFloat(custom) : amount;
    if (!amt || amt < 1) return toast.error("Please pick or enter an amount");
    setBusy(true);
    try {
      const r = await api.post("/donations", {
        amount_inr: amt,
        message,
        display_name: name || undefined,
        country: country || undefined,
      });
      toast.success(r.data.thank_you, { duration: 6000 });
      setMessage(""); setCustom(""); setAmount(101);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <section
      className="rounded-3xl bg-gradient-to-br from-rose-50 via-white to-amber-50 border-2 border-rose-200 p-6 md:p-8"
      data-testid="pay-from-heart"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 grid place-items-center rounded-2xl bg-rose-500 text-white shadow-lg">
          <Heart size={22} fill="currentColor" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-rose-700">Pay from heart</div>
          <div className="text-lg font-black text-stone-900">Keep Find2Near free for the next citizen</div>
        </div>
      </div>
      <p className="text-sm text-stone-600 mb-4 max-w-xl">Not a subscription. Not a paywall. Just gratitude — pick an amount that feels right, or write your own. Every rupee shows up on the Top Supporters wall (unless you stay anonymous).</p>

      <form onSubmit={submit} className="space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="pay-from-heart-presets">
          {PRESETS_INR.map(v => (
            <button
              key={v}
              type="button"
              onClick={() => { setAmount(v); setCustom(""); }}
              className={`text-sm font-bold px-4 py-2 rounded-full border-2 transition-colors ${(!custom && amount === v) ? "bg-rose-600 border-rose-600 text-white" : "border-rose-200 text-rose-700 hover:border-rose-400"}`}
              data-testid={`pay-from-heart-preset-${v}`}
            >
              ₹{v.toLocaleString("en-IN")}
            </button>
          ))}
          <div className="relative flex items-center">
            <span className="absolute left-3 text-stone-500 font-mono">₹</span>
            <Input
              value={custom}
              onChange={e => setCustom(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="Custom"
              inputMode="decimal"
              className="pl-7 rounded-full w-32 border-rose-200 focus-visible:ring-rose-500"
              data-testid="pay-from-heart-custom"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Input value={name} onChange={e => setName(e.target.value)}
            placeholder={user ? `Show as: ${user.name}` : "Display name (or leave blank for Anonymous)"}
            className="rounded-xl" data-testid="pay-from-heart-name" />
          <Input value={country} onChange={e => setCountry(e.target.value)}
            placeholder="Country (optional)"
            className="rounded-xl" data-testid="pay-from-heart-country" />
        </div>
        <Textarea value={message} onChange={e => setMessage(e.target.value)}
          placeholder="A short message with your donation (optional)"
          rows={2} className="rounded-xl" data-testid="pay-from-heart-message" />

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[10px] text-stone-500">
            {" "}Recorded now; payment collection goes live once a Stripe/Razorpay key is configured.
          </div>
          <Button type="submit" disabled={busy}
            className="rounded-full bg-rose-600 hover:bg-rose-700 px-6"
            data-testid="pay-from-heart-submit">
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Heart size={14} className="mr-2" fill="currentColor" />}
            {busy ? "Sending love…" : `Contribute ${custom ? `₹${custom}` : `₹${amount}`}`}
          </Button>
        </div>
      </form>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Top Supporters
// -----------------------------------------------------------------------------
function TopSupporters() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/donations/supporters").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const { supporters, totals } = data;

  return (
    <section className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="top-supporters">
      <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-white border-b border-stone-200 flex items-center gap-3">
        <div className="w-10 h-10 grid place-items-center rounded-2xl bg-amber-500 text-white shadow"><Trophy size={18} /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest font-bold text-amber-700">Top 100 supporters</div>
          <div className="text-lg font-black text-stone-900">The people, brands, and countries keeping this free</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Total received</div>
          <div className="font-mono font-black text-emerald-700">{inr(totals.amount_inr)}</div>
          <div className="text-[10px] text-stone-500">{totals.donation_count} contributions · {totals.supporter_count} supporters</div>
        </div>
      </div>
      {supporters.length === 0 ? (
        <div className="p-8 text-center text-stone-500 text-sm">
          Be the first — your name will appear here.
        </div>
      ) : (
        <ol className="divide-y divide-stone-100">
          {supporters.slice(0, 100).map((s, i) => (
            <li key={`${s.display_name}-${i}`} className="px-6 py-3 flex items-center gap-4 hover:bg-stone-50" data-testid={`supporter-${i}`}>
              <div className={`w-8 h-8 grid place-items-center rounded-full font-black text-xs shrink-0 ${i < 3 ? "bg-gradient-to-br from-amber-400 to-orange-600 text-white shadow" : "bg-stone-100 text-stone-600"}`}>
                {i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-stone-900 truncate">{s.display_name}</div>
                <div className="text-[11px] text-stone-500 flex items-center gap-2">
                  {s.country && <span className="inline-flex items-center gap-0.5"><Globe size={10} /> {s.country}</span>}
                  <span>{s.count} contribution{s.count !== 1 ? "s" : ""}</span>
                </div>
              </div>
              <div className="text-right font-mono font-black text-emerald-700 text-sm shrink-0">{inr(s.total_inr)}</div>
            </li>
          ))}
        </ol>
      )}

      {/* Static "Categories we love" panel — signals the site aspires to attract these
          contributors even while the wall is filling up. */}
      <div className="p-6 grid md:grid-cols-3 gap-3 border-t border-stone-100 bg-stone-50">
        {[
          { label: "Authorised Service Providers & Registered Brand Partners", icon: Sparkles, tone: "text-purple-700" },
          { label: "Country Governments & Civic Bodies",                       icon: Globe,    tone: "text-emerald-700" },
          { label: "Celebrity Ambassadors & Public Figures",                   icon: Trophy,   tone: "text-amber-700" },
        ].map(g => (
          <div key={g.label} className="rounded-2xl bg-white border border-stone-200 p-4 flex items-start gap-3">
            <div className="w-8 h-8 grid place-items-center rounded-xl bg-stone-100 shrink-0"><g.icon size={14} className={g.tone} /></div>
            <div className="text-xs font-bold text-stone-800">{g.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Suggestion Board
// -----------------------------------------------------------------------------
const CATS = ["general", "ui", "feature", "bug", "content", "safety", "performance"];

function SuggestionBoard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("general");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [upvoted, setUpvoted] = useState({});   // client-side memory, no server truth needed

  const refresh = () => api.get("/suggestions").then(r => setItems(r.data.suggestions || [])).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) return toast.error("Please share at least 10 characters");
    setBusy(true);
    try {
      const r = await api.post("/suggestions", {
        text, category,
        contact_email: email || undefined,
      });
      toast.success(r.data.auto_reply, { duration: 8000 });
      setText(""); setEmail("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  const upvote = async (id) => {
    if (upvoted[id]) return toast.info("You've already voted");
    try {
      await api.post(`/suggestions/${id}/upvote`);
      setUpvoted({ ...upvoted, [id]: true });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    }
  };

  return (
    <section className="rounded-3xl bg-white border border-stone-200 overflow-hidden" data-testid="suggestion-board">
      <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-white border-b border-stone-200 flex items-center gap-3">
        <div className="w-10 h-10 grid place-items-center rounded-2xl bg-emerald-600 text-white shadow"><MessageSquare size={18} /></div>
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Suggestion board</div>
          <div className="text-lg font-black text-stone-900">Tell us how to make Find2Near yours</div>
        </div>
      </div>

      <form onSubmit={submit} className="p-6 space-y-3 border-b border-stone-100">
        <Textarea value={text} onChange={e => setText(e.target.value)}
          placeholder="Your idea, wish, or concern — the team reads every one."
          rows={3} className="rounded-xl" required data-testid="suggestion-text" />
        <div className="grid md:grid-cols-3 gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-xl" data-testid="suggestion-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATS.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={email} onChange={e => setEmail(e.target.value)}
            placeholder={user ? "Reply email (optional)" : "Your email (optional)"}
            className="rounded-xl md:col-span-2" data-testid="suggestion-email" />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="suggestion-submit">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
            {busy ? "Sending…" : "Send suggestion"}
          </Button>
        </div>
      </form>

      {items.length === 0 ? (
        <div className="p-8 text-center text-stone-500 text-sm">No suggestions yet. Be the first.</div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {items.map(s => (
            <li key={s.suggestion_id} className="px-6 py-4" data-testid={`suggestion-${s.suggestion_id}`}>
              <div className="flex items-start gap-3">
                <button onClick={() => upvote(s.suggestion_id)}
                  className={`flex flex-col items-center gap-0.5 shrink-0 rounded-lg px-2 py-1 border transition-colors ${upvoted[s.suggestion_id] ? "bg-emerald-100 border-emerald-500 text-emerald-800" : "bg-stone-50 border-stone-200 text-stone-600 hover:border-emerald-500"}`}
                  data-testid={`suggestion-upvote-${s.suggestion_id}`}>
                  <ChevronUp size={12} />
                  <span className="text-[10px] font-black font-mono">{s.up_votes}</span>
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-stone-800 leading-relaxed">{s.text}</div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
                    <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{s.category}</span>
                    <span className="text-stone-500">— {s.user_name}</span>
                    <span className="text-stone-400 font-mono">{s.created_at?.slice(0, 10)}</span>
                    <span className={`ml-auto rounded-full px-2 py-0.5 ${s.status === "shipped" ? "bg-emerald-100 text-emerald-800" : s.status === "reviewing" ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-600"}`}>{s.status}</span>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Auto-Download setting card — mounted on Account page. Exported here to keep
// the file scope tight; imported by Account.jsx (see next edit).
// -----------------------------------------------------------------------------
export function AutoDownloadSetting() {
  const [enabled, setEnabled] = useState(false);
  const [maxSize, setMaxSize] = useState(20);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings/auto-download").then(r => {
      setEnabled(r.data.enabled); setMaxSize(r.data.max_size_mb);
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const save = async (nextEnabled, nextMax) => {
    setBusy(true);
    try {
      await api.put("/settings/auto-download", { enabled: nextEnabled, max_size_mb: nextMax });
      setEnabled(nextEnabled); setMaxSize(nextMax);
      toast.success(nextEnabled ? `Auto-download on for files up to ${nextMax}MB` : "Auto-download off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  if (!loaded) return null;

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="auto-download-setting">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 grid place-items-center rounded-xl bg-emerald-100 text-emerald-700 shrink-0"><Download size={16} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-stone-900 text-sm">Auto-download shared files</div>
          <div className="text-[11px] text-stone-500">Files up to <b>20MB</b> are shared free. Beyond that senders pay <b>₹1 per MB</b> — so only useful content reaches you.</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={busy}
          onClick={() => save(!enabled, maxSize)}
          className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${enabled ? "bg-emerald-500" : "bg-stone-300"}`}
          data-testid="auto-download-toggle"
        >
          <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-md ring-1 ring-black/10 transition-transform ${enabled ? "translate-x-6" : "translate-x-0"}`} />
        </button>
      </div>
      {enabled && (
        <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-3 flex-wrap">
          <span className="text-xs text-stone-600">Auto-download files up to</span>
          <Select value={String(maxSize)} onValueChange={v => save(true, parseInt(v))}>
            <SelectTrigger className="rounded-xl h-8 w-28" data-testid="auto-download-max"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[5, 10, 20, 50, 100, 200].map(mb => (
                <SelectItem key={mb} value={String(mb)}>{mb} MB</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
